import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  addCrossClientLandingCompletedExecutions,
  selectCrossClientLandingCompletedExecutionProgresses,
  selectCrossClientLandingCompletedExecutions,
  selectCrossClientLandingCompletedExecutionsLoadingMore,
  selectCrossClientLandingRunningExecutionProgresses,
  selectCrossClientLandingRunningExecutions,
  selectCrossClientLandingTeammates,
  selectCrossClientLandingTeammatesLoading,
  selectCrossClientLandingTotalCompletedExecutions,
  setCrossClientLandingCompletedExecutionsLoadingMore,
  setCrossClientLandingTotalCompletedExecutions,
} from '../../../../store/crossClient/crossClientLanding'
import {
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryPhases,
  CrossClientQueryResultStatuses,
} from '../../../../models/CrossClient'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useLazyGetCrossClientQueryExecutionsQuery } from '../../../../store/crossClient/crossClient'
import { Button } from '../../../../components/Button/Button'
import { useNavigate } from 'react-router-dom'

import { getFormattedDurationShort } from '../../../../common/duration'
import { CrossClientRunningExecutionsPoller } from '../RunningExecutions/CrossClientRunningExecutionsPoller'
import { CrossClientProgress } from '../../Progress/CrossClientProgress'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import classNames from 'classnames'
import { formatDate, getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { getInitials } from '../../../utils'
import { UserAvatar } from '../../../../components/UserAvatar/UserAvatar'
import './CrossClientLandingHistory.scss'

const SHOW_MORE_PAGE_SIZE = 5

export const CrossClientLandingHistory = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const completedExecutions = useAppSelector(selectCrossClientLandingCompletedExecutions)
  const completedExecutionProgresses = useAppSelector(selectCrossClientLandingCompletedExecutionProgresses)
  const runningExecutions = useAppSelector(selectCrossClientLandingRunningExecutions)
  const runningExecutionProgresses = useAppSelector(selectCrossClientLandingRunningExecutionProgresses)
  const completedExecutionsLoadingMore = useAppSelector(selectCrossClientLandingCompletedExecutionsLoadingMore)
  const totalCompletedExecutions = useAppSelector(selectCrossClientLandingTotalCompletedExecutions)

  const [triggerGetCrossClientExecutions] = useLazyGetCrossClientQueryExecutionsQuery()

  const getCompletedExecutions = useCallback(
    async (page: number) => {
      try {
        dispatch(setCrossClientLandingCompletedExecutionsLoadingMore(true))
        const executions = await triggerGetCrossClientExecutions({
          accountId,
          phase: CrossClientQueryPhases.COMPLETED,
          pageRequest: { page, size: SHOW_MORE_PAGE_SIZE },
        }).unwrap()
        if (!executions.data.length) {
          return
        }
        dispatch(addCrossClientLandingCompletedExecutions(executions.data))
        dispatch(setCrossClientLandingTotalCompletedExecutions(executions.page.totalElements))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientLandingCompletedExecutionsLoadingMore(false))
      }
    },
    [dispatch, accountId, triggerGetCrossClientExecutions],
  )

  const handleShowMore = async () => {
    if (completedExecutions.length >= totalCompletedExecutions) {
      return
    }
    const page = Math.floor(completedExecutions.length / SHOW_MORE_PAGE_SIZE)
    await getCompletedExecutions(page)
  }

  return (
    <>
      <CrossClientRunningExecutionsPoller />
      <div className='d-flex flex-column gap-3 align-items-center'>
        {runningExecutions.map((execution) => {
          const progress = runningExecutionProgresses.find((p) => p.executionId === execution.id)
          return <CrossClientHistoryElement key={execution.id} execution={execution} progress={progress} />
        })}
        {completedExecutions.map((execution) => {
          const progress = completedExecutionProgresses.find((p) => p.executionId === execution.id)
          return <CrossClientHistoryElement key={execution.id} execution={execution} progress={progress} />
        })}
        {completedExecutions.length < totalCompletedExecutions ? (
          <Button variant='header' className='button-small zocks-border-0.5' onClick={handleShowMore} loading={completedExecutionsLoadingMore}>
            Show more…
          </Button>
        ) : null}
      </div>
    </>
  )
}

interface CrossClientHistoryElementProps {
  execution: CrossClientQueryExecution
  progress?: CrossClientQueryExecutionProgress
}

const CrossClientHistoryElement: React.FC<CrossClientHistoryElementProps> = ({ execution, progress }) => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const teammates = useAppSelector(selectCrossClientLandingTeammates)
  const teammatesLoading = useAppSelector(selectCrossClientLandingTeammatesLoading)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const [now, setNow] = useState<Date>(new Date())
  const [hovered, setHovered] = useState<boolean>(false)

  const isRunning = execution?.phase === CrossClientQueryPhases.PROCESSING
  const userName = useMemo(() => {
    return execution.createdBy?.id === currentUser.id
      ? getUserDisplayName(currentUser)
      : getUserDisplayName(teammates.find((t) => t.id === execution.createdBy?.id))
  }, [currentUser, execution.createdBy?.id, teammates])

  useEffect(() => {
    if (isRunning) {
      const interval = setInterval(() => setNow(new Date()), 1000)
      return () => {
        clearInterval(interval)
      }
    }
  }, [isRunning])

  const handleExecutionClicked = tracked(() => navigate(`/cross-client/executions/${execution.id}`), {
    eventName: TrackEvent.X_CLIENT_HISTORY_OPENED,
    eventDetails: {
      executionId: execution.id,
    },
  })

  const succeededResultCount = progress?.statuses[CrossClientQueryResultStatuses.SUCCEEDED] ?? 0
  const noDataResultCount = progress?.statuses[CrossClientQueryResultStatuses.NO_DATA] ?? 0
  const failedResultCount = progress?.statuses[CrossClientQueryResultStatuses.FAILED] ?? 0
  const processedResultCount = succeededResultCount + noDataResultCount + failedResultCount

  return (
    <div
      className={classNames(
        'd-flex gap-3 p-3 align-items-center border border-1  zocks-border-0.75 bg-white w-100 cross-client-history-element cross-client-transition',
        {
          'zocks-border-color-primary cross-client-history-element-box-shadow': progress && hovered,
          'zocks-border-color-gray-300': !hovered,
        },
      )}
      role='button'
      onClick={handleExecutionClicked}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!progress ? (
        <div className='skeleton pt-2 zocks-border-0.5' style={{ width: '100%', height: '30px' }} />
      ) : (
        <>
          <CrossClientProgress
            className='cross-client-landing-progress-container'
            total={execution.clientIds.length}
            done={processedResultCount}
            lineWidth={35}
          />

          <div className='d-flex zocks-gap-2 justify-content-between align-items-center w-100'>
            <div className='d-flex flex-column gap-1 flex-grow-1 w-100'>
              <div className='paragraph-medium medium text-truncate cross-client-history-question'>{execution.userQuestion}</div>
              <div className='d-flex gap-3'>
                <div className='d-flex gap-1 align-items-center'>
                  {teammatesLoading && !userName ? (
                    <div className='skeleton' style={{ width: '6rem', height: '1rem' }} />
                  ) : (
                    <>
                      <UserAvatar
                        width='1rem'
                        height='1rem'
                        fontSize='0.5rem'
                        className='lead-extra-small zocks-text-white zocks-bg-color-gray-900'
                        useInitials
                        initialLetters={getInitials(userName.split(' ')[0])}
                      />
                      <div className='paragraph-extra-small'>{userName}</div>
                    </>
                  )}
                </div>
                <div className='d-flex gap-1 align-items-center'>
                  <i className='bi bi-calendar' />
                  <div className='paragraph-extra-small'>{formatDate(execution.createdAt)}</div>
                </div>
                <div className='d-flex gap-1 align-items-center'>
                  <i className='bi bi-stopwatch' />
                  {execution.phase === CrossClientQueryPhases.COMPLETED ? (
                    <div className='paragraph-extra-small zocks-text-gray-700'>{getFormattedDurationShort(execution.createdAt, execution.updatedAt)}</div>
                  ) : (
                    <div className='paragraph-extra-small zocks-text-gray-700'>{getFormattedDurationShort(execution.createdAt, now.toISOString())}</div>
                  )}
                </div>
              </div>
            </div>
            <i className={classNames('bi bi-arrow-right fs-5', { 'zocks-text-primary': progress && hovered })} />
          </div>
        </>
      )}
    </div>
  )
}
